// =============================================================================
// BillingBanner — global dunning / trial-conversion strip (SaaS billing
// Phase C, docs/specs/saas-subscription-billing.md §8). Mounted once in the
// app shell; renders ONLY while the active tenant has a live dunning episode
// (payment failed, still inside grace) or is an enrolled TRIAL nearing
// expiry — otherwise null. Reads get_billing_status, which is deliberately
// price-free so any member may see the warning; the fix CTA routes billing
// admins to /billing and tells everyone else to find their administrator.
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { isAdmin } from '@/lib/roles'
import { fmtDateZA } from '@/lib/dates'

export interface BillingStatus {
  tenant_status: string | null
  trial_expires_at: string | null
  plan_tier: string | null
  plan_name: string | null
  enrolled: boolean
  billing_method: string | null
  provider_status: string | null
  has_card_subscription: boolean
  past_due_since: string | null
  grace_days: number | null
  suspend_at: string | null
  current_period_end: string | null
}

export function useBillingStatus() {
  const { data: tenant } = useActiveTenant()
  return useQuery({
    queryKey: ['billing-status', tenant?.tenantId],
    enabled: !!tenant?.tenantId,
    // App-shell singleton: this fires on every tenant's every boot and every
    // window-focus once stale. Billing state moves daily at most, and on a
    // waking farm tablet the shared-client read stalls 15-40s — at 5 min
    // staleTime it was ~100 telemetry events/day within 2 days of shipping
    // (2026-08-18 regression). Long staleTime + offlineFirst keep the banner
    // honest without joining the wake stampede.
    staleTime: 30 * 60_000,
    networkMode: 'offlineFirst',
    queryFn: async () => {
      // A read, not a save: withReadTimeout fails fast (15s) and logs no
      // slow_save/save_timeout — a real failure surfaces as query_error.
      const { data, error } = await withReadTimeout(supabase.rpc('get_billing_status'), 'billing status')
      if (error) throw error
      return data as unknown as BillingStatus
    },
  })
}

const TRIAL_NUDGE_DAYS = 14

function Strip({ tone, children }: { tone: 'danger' | 'warn'; children: React.ReactNode }) {
  return (
    <div className={`fixed top-0 inset-x-0 z-[60] ${tone === 'danger' ? 'bg-rose-700' : 'bg-amber-600'} text-white text-xs sm:text-sm px-3 py-2 flex items-center justify-center gap-2 shadow-lg`}>
      <AlertTriangle size={14} className="shrink-0" />
      {children}
    </div>
  )
}

// Two banner states × admin/non-admin CTA × date arithmetic — irreducibly
// branchy presentational chooser, uncovered (AddonCard precedent). Suppress.
// fallow-ignore-next-line complexity
export default function BillingBanner() {
  const { data: me } = useCurrentUser()
  const { data: s } = useBillingStatus()
  if (!s) return null
  const admin = isAdmin(me?.role)

  if (s.past_due_since && s.tenant_status === 'ACTIVE') {
    return (
      <Strip tone="danger">
        <span>
          Payment failed{s.suspend_at ? <> — the app locks on <b>{fmtDateZA(s.suspend_at)}</b></> : null}.
        </span>
        {admin
          ? <Link to="/billing" className="underline font-semibold whitespace-nowrap">Fix payment →</Link>
          : <span className="whitespace-nowrap">Ask your administrator to fix payment.</span>}
      </Strip>
    )
  }

  if (s.tenant_status === 'TRIAL' && s.enrolled && s.trial_expires_at) {
    const daysLeft = Math.ceil((new Date(s.trial_expires_at).getTime() - Date.now()) / 86_400_000)
    if (daysLeft <= TRIAL_NUDGE_DAYS) {
      return (
        <Strip tone="warn">
          <span>
            Trial ends <b>{fmtDateZA(s.trial_expires_at)}</b>
            {daysLeft > 0 ? ` — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : ''}.
          </span>
          {admin
            ? <Link to="/billing" className="underline font-semibold whitespace-nowrap">Set up payment →</Link>
            : null}
        </Strip>
      )
    }
  }

  return null
}
